import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../api/client';
import { createUser, patchUser, userAction, type UserAction } from '../../api/endpoints';
import { qk, useAdminOrgs, useAdminUser } from '../../api/hooks';
import type { AdminOrg, AdminUserCard, Membership, UserInput } from '../../api/types';
import { toast } from '../../app/toast';
import { Loading } from '../../components/States';
import { fmtTime } from '../../lib/dates';
import { fmtPhone, normPhone } from '../../lib/phone';
import { ConfirmButton } from './ConfirmButton';
import { UserStatusPill } from './UserStatusPill';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Карточка пользователя или приглашение (drawEditor прототипа). */
export function UserEditor({ id, onClose }: { id: string | null; onClose: () => void }) {
  const card = useAdminUser(id);
  const orgs = useAdminOrgs();
  if ((id && !card.data) || !orgs.data)
    return (
      <section className="wiz" style={{ maxWidth: 'none', marginBottom: 22 }}>
        <Loading />
      </section>
    );
  return <EditorForm key={id ?? 'new'} card={card.data ?? null} orgs={orgs.data} onClose={onClose} />;
}

function EditorForm({
  card,
  orgs,
  onClose,
}: {
  card: AdminUserCard | null;
  orgs: AdminOrg[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [name, setName] = useState(card?.name ?? '');
  const [email, setEmail] = useState(card?.email ?? '');
  const [phone, setPhone] = useState(card?.phone ? fmtPhone(card.phone) : '');
  const [admin, setAdmin] = useState(card?.is_platform_admin ?? false);
  const [members, setMembers] = useState<Membership[]>(card?.memberships ?? []);
  const free = orgs.filter((o) => !members.some((m) => m.org_id === o.id));
  const [pick, setPick] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const isSelf = !!card?.is_self;

  const refreshLists = () => {
    void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    void qc.invalidateQueries({ queryKey: qk.orgs });
    void qc.invalidateQueries({ queryKey: ['admin', 'audit'] });
  };

  const save = useMutation({
    meta: { manualError: true },
    mutationFn: (body: UserInput) => (card ? patchUser(card.id, body) : createUser(body)),
    onSuccess: (res) => {
      refreshLists();
      if (card) {
        qc.setQueryData(qk.user(card.id), res);
        toast(t('admin.editor.saved'));
      } else toast(t('admin.editor.invited', { email: res.email }));
      onClose();
    },
    onError: (e) => {
      if (e instanceof ApiError && !e.handled) setErr(e.userMessage);
    },
  });

  const act = useMutation({
    mutationFn: ({ action }: { action: UserAction; okText: string }) => userAction(card!.id, action),
    onSuccess: (res, { okText }) => {
      qc.setQueryData(qk.user(res.id), res);
      refreshLists();
      toast(okText);
    },
  });

  const submit = () => {
    const n = name.trim();
    const em = email.trim().toLowerCase();
    const ph = normPhone(phone);
    if (!n) return setErr(t('admin.editor.err.name'));
    if (!EMAIL.test(em)) return setErr(t('admin.editor.err.email'));
    if (ph === null) return setErr(t('admin.editor.err.phone'));
    if (!admin && !members.length) return setErr(t('admin.editor.err.orgs'));
    setErr(null);
    save.mutate({
      name: n,
      email: em,
      phone: ph || null,
      is_platform_admin: admin,
      org_ids: members.map((m) => m.org_id),
    });
  };

  const addOrg = () => {
    const o = orgs.find((x) => x.id === (pick || free[0]?.id));
    if (!o) return;
    setMembers([...members, { org_id: o.id, org_name: o.name, org_type: o.type }]);
    setPick('');
  };

  const freeClinics = free.filter((o) => o.type === 'clinic');
  const freeLabs = free.filter((o) => o.type === 'lab');
  const login = card?.login;

  return (
    <section
      className="wiz"
      style={{ maxWidth: 'none', marginBottom: 22 }}
      aria-label={card ? card.name : t('admin.editor.inviteTitle')}
    >
      <h2>{card ? card.name : t('admin.editor.inviteTitle')}</h2>
      <div className="grid-3">
        <label className="field">
          {t('admin.editor.name')}
          <input
            className="input"
            value={name}
            autoComplete="off"
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label className="field">
          {t('admin.editor.email')}
          <input
            className="input"
            type="email"
            value={email}
            autoComplete="off"
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <label className="field">
          {t('admin.editor.phone')}
          <input
            className="input"
            type="tel"
            value={phone}
            autoComplete="off"
            placeholder={t('admin.editor.phonePlaceholder')}
            onChange={(e) => setPhone(e.target.value)}
            onBlur={() => {
              const p = normPhone(phone);
              if (p) setPhone(fmtPhone(p));
            }}
          />
        </label>
      </div>
      <p className="muted small" style={{ margin: '6px 0 0' }}>
        {card ? t('admin.editor.phoneHintEdit') : t('admin.editor.phoneHintNew')}
      </p>

      <div style={{ marginTop: 18 }}>
        <div className="muted small" style={{ fontWeight: 500, marginBottom: 4 }}>
          {t('admin.editor.orgsTitle')}
        </div>
        <ul className="files">
          {members.length ? (
            members.map((m) => (
              <li key={m.org_id}>
                <span className="nm">{m.org_name}</span>
                <span className="tag">{t(`admin.orgType.${m.org_type}`)}</span>
                <span className="muted small">
                  {t('admin.editor.roleIs', { role: t(`admin.roleOf.${m.org_type}`) })}
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-quiet"
                  onClick={() => setMembers(members.filter((x) => x.org_id !== m.org_id))}
                >
                  {t('admin.editor.removeOrg')}
                </button>
              </li>
            ))
          ) : (
            <li className="muted">{t('admin.editor.noOrgs')}</li>
          )}
        </ul>
        {free.length > 0 && (
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <select
              className="input"
              style={{ maxWidth: 340 }}
              value={pick || free[0].id}
              aria-label={t('admin.editor.orgSelect')}
              onChange={(e) => setPick(e.target.value)}
            >
              {freeClinics.length > 0 && (
                <optgroup label={t('admin.editor.clinicsGroup')}>
                  {freeClinics.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </optgroup>
              )}
              {freeLabs.length > 0 && (
                <optgroup label={t('admin.editor.labsGroup')}>
                  {freeLabs.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
            <button type="button" className="btn" onClick={addOrg}>
              {t('admin.editor.addOrg')}
            </button>
          </div>
        )}
        <p className="muted small" style={{ margin: '8px 0 0' }}>
          {t('admin.editor.roleHint')}
        </p>
      </div>

      <label className="check" style={{ marginTop: 16 }}>
        <input
          type="checkbox"
          checked={admin}
          disabled={isSelf}
          onChange={(e) => setAdmin(e.target.checked)}
        />
        {t('admin.editor.admin')}
        {isSelf ? t('admin.editor.adminSelf') : ''}
      </label>

      {card && login && (
        <div className="section" style={{ marginTop: 20, paddingBottom: 0 }}>
          <h3 style={{ marginBottom: 10 }}>{t('admin.login.title')}</h3>
          <dl className="kv">
            <dt>{t('admin.login.status')}</dt>
            <dd>
              <UserStatusPill status={card.status} />
            </dd>
            <dt>{t('admin.login.password')}</dt>
            <dd>
              {login.password_set === null
                ? t('admin.login.passwordUnknown')
                : login.password_set
                  ? t('admin.login.passwordSet')
                  : t('admin.login.passwordNotSet')}
            </dd>
            <dt>{t('admin.login.lastLogin')}</dt>
            <dd>{card.last_login_at ? fmtTime(card.last_login_at) : t('admin.login.never')}</dd>
            <dt>{t('admin.login.link')}</dt>
            <dd>
              {login.link === 'valid'
                ? t('admin.login.linkValid', {
                    time: login.link_expires_at ? fmtTime(login.link_expires_at) : '—',
                  })
                : login.link === 'expired'
                  ? t('admin.login.linkExpired', {
                      time: login.link_expires_at ? fmtTime(login.link_expires_at) : '—',
                    })
                  : t('admin.login.linkNone')}
            </dd>
            {login.locked && (
              <>
                <dt>{t('admin.login.locked')}</dt>
                <dd>{t('admin.login.lockedText')}</dd>
              </>
            )}
          </dl>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
            {card.status === 'invited' && (
              <button
                type="button"
                className="btn"
                disabled={act.isPending}
                onClick={() =>
                  act.mutate({ action: 'invite', okText: t('admin.login.resent', { email: card.email }) })
                }
              >
                {t('admin.login.resend')}
              </button>
            )}
            {login.locked && (
              <button
                type="button"
                className="btn"
                disabled={act.isPending}
                onClick={() => act.mutate({ action: 'unlock', okText: t('admin.login.unlocked') })}
              >
                {t('admin.login.unlock')}
              </button>
            )}
            {card.status === 'active' && !isSelf && (
              <ConfirmButton
                label={t('admin.login.reset')}
                confirmLabel={t('admin.login.resetConfirm')}
                disabled={act.isPending}
                onConfirm={() =>
                  act.mutate({
                    action: 'reset-password',
                    okText: t('admin.login.resetDone', { email: card.email }),
                  })
                }
              />
            )}
            {!isSelf &&
              (card.status === 'blocked' ? (
                <ConfirmButton
                  quiet
                  label={t('admin.login.unblock')}
                  confirmLabel={t('admin.login.unblockConfirm')}
                  disabled={act.isPending}
                  onConfirm={() =>
                    act.mutate({ action: 'unblock', okText: t('admin.login.unblocked', { name: card.name }) })
                  }
                />
              ) : (
                <ConfirmButton
                  quiet
                  label={t('admin.login.block')}
                  confirmLabel={t('admin.login.blockConfirm')}
                  disabled={act.isPending}
                  onConfirm={() =>
                    act.mutate({ action: 'block', okText: t('admin.login.blocked', { name: card.name }) })
                  }
                />
              ))}
          </div>
          <p className="muted small" style={{ margin: '10px 0 0' }}>
            {t('admin.login.hint')}
          </p>
        </div>
      )}

      {err && (
        <div className="err" role="alert">
          {err}
        </div>
      )}
      <div className="wiz-foot">
        <button type="button" className="btn" onClick={onClose}>
          {card ? t('common.close') : t('common.cancel')}
        </button>
        <button type="button" className="btn btn-primary" disabled={save.isPending} onClick={submit}>
          {card ? t('admin.editor.save') : t('admin.editor.sendInvite')}
        </button>
      </div>
    </section>
  );
}
